import { Fragment } from "react";
import { Typography, Link, Box } from "@mui/material";
import { stacksUrl } from "~/pages/integrations/IntegrationsSetup/IntegrationSetupPage";
import { awsQuickcreateUrl } from "~/pages/integrations/aws/utils";
import { CanDelete } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Delete";
import { CanTriggerAction } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/TriggerAction";
import { CanAssign } from "~/lib/iam/actions/cnspec/policy/PolicyResolver/Assign";
import { AwsIntegration } from "../../AwsIntegrationServerlessPage";

interface DetailedMessageProps {
  awsIntegration: AwsIntegration;
}

export function AwsIntegrationServerlessMessage({
  awsIntegration,
}: DetailedMessageProps) {
  let ebs = false;

  if (awsIntegration?.configurationOptions?.scanConfiguration != undefined) {
    if (
      awsIntegration.configurationOptions.scanConfiguration.ec2ScanOptions !=
      undefined
    ) {
      ebs =
        awsIntegration.configurationOptions.scanConfiguration.ec2ScanOptions
          .ebsVolumeScan;
    }
  }

  switch (awsIntegration.status) {
    case "WAITING_FOR_SETUP":
      return (
        <Box mt={2}>
          <CanAssign
            fallback={
              <Typography variant="caption" display="block">
                Waiting for admin to launch the CloudFormation stack.
              </Typography>
            }
          >
            <Link
              color="secondary.main"
              href={awsQuickcreateUrl(
                awsIntegration.configurationOptions,
                awsIntegration.token,
              )}
              target="_blank"
              sx={{
                position: "absolute",
                top: 15,
                right: 45,
                fontWeight: 500,
                textTransform: "uppercase",
                color: "#4fc3f7",
              }}
            >
              Launch AWS CloudFormation
            </Link>
            <Typography variant="caption" display="block">
              Please launch the CloudFormation stack on AWS and complete the
              installation.
            </Typography>
          </CanAssign>
          <Typography variant="caption" display="block">
            Once the stack is launched on AWS, it can take a few minutes for the
            status to update below.
          </Typography>
        </Box>
      );
    case "SETUP_IN_PROGRESS":
      return (
        <Box mt={2}>
          <Typography variant="caption" display="block">
            Waiting for AWS CloudFormation to complete. This step usually takes
            about three minutes.
          </Typography>
          <CanAssign>
            <Typography variant="caption" display="block">
              Consider activating some policies while you wait.
            </Typography>
          </CanAssign>
        </Box>
      );

    case "DELETED":
      return (
        <Box mt={2}>
          <Typography variant="caption" display="block">
            AWS CloudFormation delete detected.
          </Typography>
          <CanDelete>
            <Typography variant="caption" display="block">
              Please recreate{" "}
              <Link
                color="secondary.light"
                href={awsQuickcreateUrl(
                  awsIntegration.configurationOptions,
                  awsIntegration.token,
                )}
                target="_blank"
              >
                the stack
              </Link>{" "}
              or delete the integration (see configuration tab).
            </Typography>
          </CanDelete>
        </Box>
      );
    case "MISSING":
      return (
        <Box mt={2}>
          <Typography variant="caption" display="block">
            No ping detected in the last hour.
          </Typography>
          <CanTriggerAction>
            <Typography variant="caption" display="block">
              Please check on{" "}
              <Link
                color="secondary.light"
                href={stacksUrl(awsIntegration.configurationOptions.region)}
                target="_blank"
              >
                the stack.
              </Link>{" "}
            </Typography>

            <Typography variant="caption" display="block">
              If the problem persists, please contact support@mondoo.com for
              help.
            </Typography>
          </CanTriggerAction>
        </Box>
      );
    case "ERROR": //fallthrough
    case "WARNING": //fallthrough
    case "REGISTERING": //fallthrough
    case "CONFIGURING": //fallthrough
    case "ACTIVE": //fallthrough
    default:
      return <Fragment />;
  }
}
